/**
 * Minimal Zustand + Immer store template.
 *
 * Three pieces, deliberately separated:
 *   1. `defineStore` — declare initial state, the full write set as immer draft
 *      mutators, and an optional localStorage key.
 *   2. The selector hook (`handle.useStore`) — components subscribe to one
 *      slice and re-render only when it changes.
 *   3. Immutable updates — every action runs through immer `produce`, so the
 *      mutator reads like plain assignment while state stays immutable.
 *
 * The engine is React-free (zustand/vanilla); the hook is synthesized over it
 * with `useSyncExternalStore`. This mirrors dsh's packages/client/store without
 * its cordis-specific types.
 */

import { useRef, useSyncExternalStore } from 'react'
import { createStore as createZustandStore, type StoreApi } from 'zustand/vanilla'
import { subscribeWithSelector } from 'zustand/middleware'
import { shallow } from 'zustand/shallow'
import { freeze, produce } from 'immer'

export { shallow }

/** Selector hook over a store: pick a slice, re-render only when it changes. */
export type SnapshotSelectorHook<T> =
  <S>(selector: (state: T) => S, isEqual?: (a: S, b: S) => boolean) => S

/** Writable store face. `update` accepts an immer draft mutator. */
export interface SnapshotStore<T> {
  getState(): T
  update(mutator: (draft: T) => void): void
  setState(next: T): void
  subscribe(listener: () => void): () => void
}

/**
 * Create a bare store. `persist` writes the whole value as JSON to
 * localStorage; failures (quota, private mode) only disable persistence.
 * @param init - initial state.
 * @param opts - optional persistence key.
 * @returns the store.
 */
export function createSnapshotStore<T>(init: T, opts?: { persist?: string }): SnapshotStore<T> {
  const api: StoreApi<T> = createZustandStore<T>()(subscribeWithSelector(() => init))
  if (opts?.persist !== undefined) attachPersistence(api, opts.persist)
  return {
    getState: () => api.getState(),
    subscribe: (listener) => api.subscribe(listener),
    // immer produce (not zustand's partial merge) so scalar and array roots
    // replace correctly and updates stay immutable.
    update: (mutator) => {
      api.setState(produce(api.getState(), (draft) => { mutator(draft as T) }), true)
    },
    setState: (next) => { api.setState(devFreeze(next), true) },
  }
}

/** Actions table: pure immer draft transforms, the store's complete write set. */
export type ActionsDecl<T> = Record<string, (draft: T, ...params: any[]) => void>

/** Actions with the framework-owned draft parameter baked away. */
export type BakedActions<T, A extends ActionsDecl<T>> = {
  [K in keyof A]: A[K] extends (draft: T, ...params: infer P) => void ? (...params: P) => void : never
}

/** Store declaration: fresh-state factory, optional persist key, actions. */
export interface StoreSpec<T, A extends ActionsDecl<T>> {
  init: () => T
  persist?: string
  actions: A
}

/** The value `defineStore` returns: selector hook, baked actions, raw reads. */
export interface StoreHandle<T, A extends ActionsDecl<T>> {
  readonly useStore: SnapshotSelectorHook<T>
  readonly actions: BakedActions<T, A>
  getState(): T
  subscribe(listener: () => void): () => void
}

/**
 * Declare a store.
 * @param spec - init lambda, optional persist key, actions table.
 * @returns the store handle.
 */
export function defineStore<T, A extends ActionsDecl<T>>(
  spec: StoreSpec<T, A> & { actions: A & ActionsDecl<T> },
): StoreHandle<T, A> {
  const store = createSnapshotStore<T>(
    spec.init(),
    spec.persist !== undefined ? { persist: spec.persist } : undefined,
  )
  const actions = {} as Record<string, (...params: unknown[]) => void>
  for (const key of Object.keys(spec.actions)) {
    const mutate = spec.actions[key] as (draft: T, ...params: unknown[]) => void
    actions[key] = (...params: unknown[]) => {
      store.update((draft) => { mutate(draft, ...params) })
    }
  }
  return {
    useStore: createSelectorHook(store),
    actions: actions as BakedActions<T, A>,
    getState: () => store.getState(),
    subscribe: (listener) => store.subscribe(listener),
  }
}

/**
 * Synthesize a selector hook over a store. The selected value is cached by
 * state identity; when the state changes but the slice compares equal, the
 * previous reference is kept so `useSyncExternalStore` does not re-render.
 * @param store - the snapshot store to read from.
 * @returns the selector hook.
 */
function createSelectorHook<T>(store: SnapshotStore<T>): SnapshotSelectorHook<T> {
  return function useStore<S>(selector: (state: T) => S, isEqual?: (a: S, b: S) => boolean): S {
    const selectorRef = useRef(selector)
    selectorRef.current = selector
    const isEqualRef = useRef(isEqual)
    isEqualRef.current = isEqual
    const cache = useRef<{ state: T; value: S } | undefined>(undefined)

    const getSelection = (): S => {
      const state = store.getState()
      const cached = cache.current
      if (cached !== undefined && cached.state === state) return cached.value
      const value = selectorRef.current(state)
      const equal = isEqualRef.current ?? shallow
      if (cached !== undefined && equal(cached.value as never, value as never)) {
        cache.current = { state, value: cached.value }
        return cached.value
      }
      cache.current = { state, value }
      return value
    }

    return useSyncExternalStore(store.subscribe, getSelection, getSelection)
  }
}

/** Whole-value JSON persistence to localStorage (non-fatal on any failure). */
function attachPersistence<T>(api: StoreApi<T>, name: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    const raw = localStorage.getItem(name)
    if (raw !== null) api.setState(devFreeze(JSON.parse(raw) as T), true)
  } catch (error) {
    console.error(`store '${name}' rehydration failed:`, error)
  }
  api.subscribe((state) => {
    try {
      localStorage.setItem(name, JSON.stringify(state))
    } catch (error) {
      console.error(`store '${name}' persistence failed:`, error)
    }
  })
}

/** Deep-freeze wholesale-set state outside production (set() skips immer). */
function devFreeze<T>(value: T): T {
  if (import.meta.env.PROD) return value
  return freeze(value, true)
}
